import { useState } from "react"
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCalendarDays,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import ReactDatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { DateTime } from "luxon"

const toDate = (isoDate) =>
  isoDate ? DateTime.fromISO(isoDate).toJSDate() : null
const toIsoDate = (date) => (date ? DateTime.fromJSDate(date).toISODate() : "")

/*
  One control instead of a preset dropdown plus two bare date inputs: presets
  on the left apply immediately, the calendar on the right is for a custom
  range and needs an explicit Apply so a half-made selection never fires a
  query. `getCustomSeed` mirrors the seeding the page already did for the old
  custom inputs — the calendar opens on the range already on screen rather
  than a blank one.
*/
const DateRangeControl = ({
  presets,
  customLabel,
  value,
  triggerLabel,
  getCustomSeed,
  onSelectPreset,
  onApplyCustom,
  disabled = false,
  title,
}) => {
  const [pendingRange, setPendingRange] = useState([null, null])
  const [pendingStart, pendingEnd] = pendingRange

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300"
            disabled={disabled}
            title={title}
          >
            <FontAwesomeIcon
              className="h-3.5 w-3.5 text-secondary_dark"
              icon={faCalendarDays}
            />
            {triggerLabel}
            <FontAwesomeIcon
              className="h-2.5 w-2.5 text-gray-400"
              icon={faChevronDown}
            />
          </PopoverButton>

          <PopoverPanel
            anchor="bottom end"
            className="z-40 mt-2 flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
          >
            <div className="flex max-h-[440px] w-48 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-gray-100 p-2">
              {presets.map(({ value: presetValue, label }) => (
                <button
                  key={presetValue}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    value === presetValue
                      ? "bg-secondary/10 font-semibold text-secondary_dark"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                  type="button"
                  onClick={() => {
                    onSelectPreset(presetValue)
                    close()
                  }}
                >
                  {label}
                </button>
              ))}
              <button
                className={`whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  value === customLabel
                    ? "bg-secondary/10 font-semibold text-secondary_dark"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
                type="button"
                onClick={() => {
                  const [seedFrom, seedTo] = getCustomSeed()
                  setPendingRange([toDate(seedFrom), toDate(seedTo)])
                }}
              >
                {customLabel}
              </button>
            </div>

            <div className="p-3">
              <ReactDatePicker
                inline
                selectsRange
                endDate={pendingEnd}
                maxDate={new Date()}
                monthsShown={2}
                openToDate={pendingStart || new Date()}
                startDate={pendingStart}
                onChange={(range) => setPendingRange(range)}
              />
              <div className="mt-2 flex justify-end gap-2 border-t border-gray-100 pt-2">
                <button
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-50"
                  type="button"
                  onClick={() => close()}
                >
                  Cancel
                </button>
                <button
                  className="rounded-md bg-secondary_dark px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={!pendingStart || !pendingEnd}
                  type="button"
                  onClick={() => {
                    onApplyCustom(
                      toIsoDate(pendingStart),
                      toIsoDate(pendingEnd)
                    )
                    close()
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  )
}

export default DateRangeControl
